import React from 'react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Brush
} from 'recharts';
import useDownloadChart from "../../hooks/useDownloadChart";

const TimeSeriesChart = ({ data, filters }) => {
  const { downloadChart } = useDownloadChart();
  const chartId = "timeSeriesChart";

  if (!data || data.length === 0) {
    return (
      <div className="text-center py-5 text-muted">
        No time series data available
      </div>
    );
  }

  // 🧠 Detect data presence
  const hasAttendance = data.some((d) => d.total_attendance !== undefined);
  const hasCollections = data.some((d) => d.total_collection !== undefined);

  // 🧩 Merge attendance and collections by month (avoid duplicates)
  const mergedByMonth = {};
  data.forEach((item) => {
    const month = item.month;
    if (!mergedByMonth[month]) {
      mergedByMonth[month] = {
        month,
        total_attendance: 0,
        total_collection: 0,
      };
    }

    if (item.total_attendance !== undefined) {
      mergedByMonth[month].total_attendance += item.total_attendance;
    }
    if (item.total_collection !== undefined) {
      mergedByMonth[month].total_collection += item.total_collection;
    }
  });

  const chartData = Object.values(mergedByMonth).map((d) => ({
    month: d.month,
    total_attendance:
      d.total_attendance > 0 ? Math.round(d.total_attendance) : undefined,
    total_collection:
      d.total_collection > 0 ? Math.round(d.total_collection) : undefined,
  }));

  // 📊 Calculate scales
  const maxAttendance = hasAttendance
    ? Math.max(...chartData.map((d) => d.total_attendance || 0))
    : 0;
  const maxCollection = hasCollections
    ? Math.max(...chartData.map((d) => d.total_collection || 0))
    : 0;

  const scaleRatio =
    hasAttendance && hasCollections && maxAttendance > 0
      ? maxCollection / maxAttendance
      : 0;

  // 🎨 Dynamic Chart Title
  let title = "Attendance and Collections Over Time";
  if (filters?.data_type === "attendance") title = "Attendance Over Time";
  else if (filters?.data_type === "collections") title = "Collections Over Time";

  return (
    <div className="card shadow-sm mb-4">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h2 className="h5 text-dark mb-0">{title}</h2>
          <button
            onClick={() =>
              downloadChart(chartId, "time_series_analysis_chart_report", filters)
            }
            className="btn btn-sm btn-outline-primary"
          >
            Download
          </button>
        </div>

        <div id={chartId} style={{ height: 400 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="month"
                tick={{ fontSize: 12 }}
                label={{
                  value: "Month",
                  position: "insideBottom",
                  offset: -5,
                  fontSize: 12,
                }}
              />

              {hasAttendance && (
                <YAxis
                  yAxisId="left"
                  orientation="left"
                  domain={[0, maxAttendance * 1.1]}
                  label={{
                    value: "Attendance",
                    angle: -90,
                    position: "insideLeft",
                    style: { textAnchor: "middle" },
                  }}
                />
              )}

              {hasCollections && (
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  domain={[0, maxCollection * 1.1]}
                  label={{
                    value: "Collections (KSH)",
                    angle: 90,
                    position: "insideRight",
                    style: { textAnchor: "middle" },
                  }}
                />
              )}

              <Tooltip
                formatter={(value, name) => {
                  if (name === "total_collection") {
                    return [`Kshs.${value.toLocaleString()}`, "Collections"];
                  }
                  return [value, name === "total_attendance" ? "Attendance" : name];
                }}
                labelFormatter={(value) => `Month: ${value}`}
              />

              <Legend verticalAlign="top" height={36} />

              {hasAttendance && (
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="total_attendance"
                  name="Attendance"
                  stroke="#8884d8"
                  strokeWidth={2}
                  dot={{ r: 4 }}
                  activeDot={{
                    r: 6,
                    stroke: "#8884d8",
                    strokeWidth: 2,
                    fill: "#fff",
                  }}
                />
              )}

              {hasCollections && (
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="total_collection"
                  name="Collections"
                  stroke="#82ca9d"
                  strokeWidth={2}
                  strokeDasharray={hasAttendance ? "3 3" : ""}
                  dot={{ r: 4 }}
                  activeDot={{
                    r: 6,
                    stroke: "#82ca9d",
                    strokeWidth: 2,
                    fill: "#fff",
                  }}
                />
              )}

              <Brush dataKey="month" height={30} stroke="#8884d8" />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {hasAttendance && hasCollections && (
          <div className="d-flex justify-content-between flex-wrap small text-muted mt-2">
            <p className="mb-1">Note: Collections are shown in KSH on the right axis</p>
            <div className="bg-light border-start border-primary ps-2 rounded">
              Scale ratio: Kshs.1 = {scaleRatio.toFixed(1)} attendance points
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TimeSeriesChart;
